import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { BrowserAuthError } from '@azure/msal-browser';
import Spinner from '@/common/Spinner';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { setupInterceptors } from '@/api/clients/axios';
import { setupGatewayApiAuth } from '@/api/clients/gatewayAxios';
import { postGetTokenDataFromSsoToken, postLogin } from '@/api/endpoints';
import { UserSessionModel } from '@/db/models';
import { UserSessionHelper, WebViewHelper } from '@/library';
import { getValidGwApiAccessToken } from '@/auth/GatewayApiAccessToken';
import { loginRequest } from '@/auth/config';
import { appVersion, isIOSWebView, isProductionEnv, onlineStatus } from '@/utils';
import { FetchTokenError } from '@/errors';
import { getLoginOperationalError } from './getLoginOperationalError';
import titleistLogo from '../../assets/images/titleist-logo.png';
import styles from './Login.module.scss';

const Login = () => {
  const { instance } = useMsal();
  const { goToTourBagPage } = useAppNavigation();
  const [isInitializing, setIsInitializing] = useState(true);
  const [showSSOButton, setShowSSOButton] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showAuthError, setShowAuthError] = useState(false);
  const [showOpError, setShowOpError] = useState('');
  const [email, setEmail] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [showManualLoginError, setShowManualLoginError] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    // Expose iOS bridge callback for auto-login
    (window as any).iOSLoginBridgeResponse = iOSLoginBridgeResponse;

    const initLoginFlow = async () => {
      const userSession = await UserSessionHelper.getUser();
      // If session exists redirect to landing page
      if (userSession?.email && userSession?.token) {
        goToTourBagPage();
        return;
      }

      // No session found check if the app is ran in an iOS WebView
      if (isIOSWebView) {
        try {
          WebViewHelper.iOSLoginRequest();
        } catch (error) {
          console.error('iOS bridge not available, falling back to PWA login flow:', error);
          // TODO: Should we close the WebView or fallback to PWA SSO?
          setIsInitializing(false);
          timer = setTimeout(() => setShowSSOButton(true), 500);
        }
        return;
      }

      // Standard PWA login
      setIsInitializing(false);
      timer = setTimeout(() => setShowSSOButton(true), 500);
    };

    initLoginFlow();

    return () => {
      clearTimeout(timer);
      delete window.iOSLoginBridgeResponse;
    };
    // TODO: Fix use effect to removing this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setupUserSession = async (user: UserSessionModel) => {
    setupInterceptors(user.token);
    if (!isIOSWebView) {
      // iOS handles the MSAL instance on the native app
      setupGatewayApiAuth(instance);
    }
    await UserSessionHelper.setUser(user);
    return user;
  };

  const handleSSO = async () => {
    setShowSpinner(true);

    try {
      const activeAccount = await instance.loginPopup(loginRequest);

      // request token for AWS Gateway API and store it in the session.
      // can be used later for adding/fetching club images from S3
      instance.setActiveAccount(activeAccount.account);
      setupGatewayApiAuth(instance, activeAccount.account);
      try {
        await getValidGwApiAccessToken(instance, activeAccount.account);
      } catch (error) {
        console.warn('Gateway API token acquisition failed. Continuing with base login flow.', error);
      }

      const user = new UserSessionModel();
      user.email = activeAccount.account.username;
      user.name = activeAccount.account.name || '';
      user.timestamp = new Date();

      const token = await postGetTokenDataFromSsoToken(activeAccount.idToken);
      user.token = token;

      await setupUserSession(user);
      goToTourBagPage();
    } catch (error) {
      console.error('Failed to authenticate through SSO:', error);
      // reset errors
      setShowAuthError(false);
      setShowOpError('');
      // filter user closed popup
      if (error instanceof BrowserAuthError && error.errorCode === 'user_cancelled') {
        return;
      } else if (!onlineStatus.get()) {
        setShowOpError('No internet connection. Please check your network.');
      } else if (error instanceof FetchTokenError) {
        // filter popup auth errors from op errors
        setShowOpError(getLoginOperationalError(error));
      } else {
        setShowAuthError(true); // default
      }
    } finally {
      setShowSpinner(false);
    }
  };

  const iOSLoginBridgeResponse = async (userEmail: string, idToken: string) => {
    // In iOS, show spinner instead of login button since SSO runs natively
    setShowSpinner(true);
    setIsInitializing(false);
    try {
      const user = new UserSessionModel();
      user.email = userEmail;
      user.name = userEmail.split('@')[0] || 'User'; // Use first part of email as username
      user.timestamp = new Date();
      let token = '';
      token = await postGetTokenDataFromSsoToken(idToken);
      user.token = token;
      await setupUserSession(user);
      goToTourBagPage();
    } catch (error) {
      console.error('Failed to authenticate through iOS: ', error);
      const errorMessage = getLoginOperationalError(error);
      console.error('Error while logging in with iOSLogin credentials', errorMessage);
      // if the iOS login fails, we send the user back to iOS shell
      const iOSErrorMessage = `Error: AppVersion=${appVersion} Params=${{ userEmail, idToken }} Message=${error}`;
      (window as any).webkit.messageHandlers.iOSCloseView.postMessage(JSON.stringify(iOSErrorMessage));
    }
  };

  // This function is available in dev and UAT only for testing purposes
  const handleManualLogin = async () => {
    if (!email || !password) {
      // if either email or password are missing show an error
      setShowManualLoginError(true);
      return;
    }
    setShowManualLoginError(false);
    setShowSpinner(true);
    try {
      const token = await postLogin(email, password);
      // setup user account
      const user = new UserSessionModel();
      user.email = email;
      user.name = email.split('@')[0] || 'User'; // use first part of email as username
      user.timestamp = new Date();
      user.token = token; // grab Token from manual login response
      // setup token for session, indexed db data, and user session
      setupInterceptors(user.token);
      await UserSessionHelper.setUser(user);
      goToTourBagPage();
    } catch (error) {
      console.error('Failed to authenticate through Skip SSO: ', error);
      if (!onlineStatus.get()) {
        setShowOpError('No internet connection. Please check your network.');
      } else {
        setShowOpError(getLoginOperationalError(error));
      }
    } finally {
      setShowSpinner(false);
    }
  };

  if (isInitializing) return null;

  return (
    <main className={styles.body}>
      <div className={styles.content}>
        <img src={titleistLogo} alt="Titleist Logo" className={styles.titleistLogo} />
        <div className={styles.appName}>Tour</div>
        {!showSpinner && (
          <>
            <button className={`${styles.singleSignOn} ${showSSOButton ? styles.showSignOn : styles.hideSignOn}`} onClick={handleSSO}>
              Single Sign On
            </button>
            {!isProductionEnv && (
              <>
                <div className={styles.manualLogin}>
                  Email <input id="username" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="off" />
                  Password <input id="password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="off" />
                  <button id="loginButton" onClick={handleManualLogin}>
                    Login
                  </button>
                </div>
                <span style={{ marginTop: '20px' }}>{appVersion}</span>
              </>
            )}
          </>
        )}
        {showSpinner && (
          <div className={styles.loading}>
            <Spinner className={styles.spinner} />
            <div>Signing in...</div>
          </div>
        )}
        {showAuthError && <span className={styles.invalidUser}>That user account is not registered. Please contact Titleist for support.</span>}
        {showOpError && <span className={styles.invalidUser}>{showOpError}</span>}
        {showManualLoginError && <span>Enter email and password to manually log in</span>}
      </div>
    </main>
  );
};

export default Login;
